<template>
  <div>
    <Head title="Edit Periode Pertanggungjawaban" />

    <h1 class="mb-8 text-2xl font-bold">
      <Link class="text-indigo-400 hover:text-indigo-600" href="/accountability-periodes">Periode Pertanggungjawaban</Link>
      <span class="font-medium text-indigo-400">/</span> Edit Data
    </h1>

    <div class="w-full bg-white rounded-md shadow">
      <form @submit.prevent="update">
        <div class="flex flex-wrap p-8 -mb-8 -mr-6">
          <select-input
            v-model="form.jenis"
            :error="form.errors.jenis"
            class="w-full pb-8 pr-6 lg:w-1/3"
            label="Jenis Beasiswa"
            @change="handleJenisChange"
          >
            <option value="KCS">KCS</option>
            <option value="Juare">Juare</option>
          </select-input>

          <div class="flex flex-col w-full pb-8 pr-6 lg:w-1/3">
            <label for="tanggal_awal_laporan">Tgl. Awal Laporan</label>
            <Datepicker
              v-model="tanggal_awal_laporan"
              :enable-time-picker="false"
              :utc="false"
              class="pt-2"
              format="dd-MM-yyyy"
            />
            <div v-if="form.errors.tanggal_awal_laporan" class="mt-1 text-sm text-red-600">{{ form.errors.tanggal_awal_laporan }}</div>
          </div>

          <div class="flex flex-col w-full pb-8 pr-6 lg:w-1/3">
            <label for="tanggal_akhir_laporan">Tgl. Akhir Laporan</label>
            <Datepicker
              v-model="tanggal_akhir_laporan"
              :enable-time-picker="false"
              :utc="false"
              class="pt-2"
              format="dd-MM-yyyy"
            />
            <div v-if="form.errors.tanggal_akhir_laporan" class="mt-1 text-sm text-red-600">{{ form.errors.tanggal_akhir_laporan }}</div>
          </div>

          <div class="flex flex-col w-full pb-8 pr-6 lg:w-1/3">
            <label for="tanggal_awal_pj">Tgl. Awal Pertanggungjawaban</label>
            <Datepicker
              v-model="tanggal_awal_pj"
              :enable-time-picker="false"
              :utc="false"
              class="pt-2"
              format="dd-MM-yyyy"
            />
            <div v-if="form.errors.tanggal_awal_pj" class="mt-1 text-sm text-red-600">{{ form.errors.tanggal_awal_pj }}</div>
          </div>

          <div class="flex flex-col w-full pb-8 pr-6 lg:w-1/3">
            <label for="tanggal_akhir_pj">Tgl. Akhir Pertanggungjawaban</label>
            <Datepicker
              v-model="tanggal_akhir_pj"
              :enable-time-picker="false"
              :utc="false"
              class="pt-2"
              format="dd-MM-yyyy"
            />
            <div v-if="form.errors.tanggal_akhir_pj" class="mt-1 text-sm text-red-600">{{ form.errors.tanggal_akhir_pj }}</div>
          </div>

          <text-input
            v-model.number="form.tahun_anggaran"
            type="number"
            :error="form.errors.tahun_anggaran"
            class="w-full pb-8 pr-6 lg:w-1/3"
            label="Tahun Anggaran"
            required
          />

          <text-input
            v-if="form.jenis === 'KCS'"
            v-model.number="form.nominal_kcs"
            type="number"
            :error="form.errors.nominal_kcs"
            class="w-full pb-8 pr-6 lg:w-1/3"
            label="Nominal KCS (Maksimal Beasiswa)"
          />

          <template v-if="form.jenis === 'Juare'">
            <text-input
              v-model.number="form.nominal_pts_prestasi"
              type="number"
              :error="form.errors.nominal_pts_prestasi"
              class="w-full pb-8 pr-6 lg:w-1/3"
              label="Nominal PTS Prestasi (Maksimal Beasiswa)"
            />
            <text-input
              v-model.number="form.nominal_pts_non_prestasi"
              type="number"
              :error="form.errors.nominal_pts_non_prestasi"
              class="w-full pb-8 pr-6 lg:w-1/3"
              label="Nominal PTS PraSejahtera (Maksimal Beasiswa)"
            />
            <text-input
              v-model.number="form.nominal_ptnl_prestasi"
              type="number"
              :error="form.errors.nominal_ptnl_prestasi"
              class="w-full pb-8 pr-6 lg:w-1/3"
              label="Nominal PTNL Prestasi (Maksimal Beasiswa)"
            />
            <text-input
              v-model.number="form.nominal_ptnl_non_prestasi"
              type="number"
              :error="form.errors.nominal_ptnl_non_prestasi"
              class="w-full pb-8 pr-6 lg:w-1/3"
              label="Nominal PTNL PraSejahtera (Maksimal Beasiswa)"
            />
            <text-input
              v-model.number="form.nominal_ptn_prestasi"
              type="number"
              :error="form.errors.nominal_ptn_prestasi"
              class="w-full pb-8 pr-6 lg:w-1/3"
              label="Nominal PTN & PTKL Prestasi (Maksimal Beasiswa)"
            />
            <text-input
              v-model.number="form.nominal_ptn_non_prestasi"
              type="number"
              :error="form.errors.nominal_ptn_non_prestasi"
              class="w-full pb-8 pr-6 lg:w-1/3"
              label="Nominal PTN & PTKL PraSejahtera (Maksimal Beasiswa)"
            />
          </template>
        </div>
        <div class="flex items-center justify-end px-8 py-4 border-t border-gray-100 bg-gray-50">
          <loading-button :loading="form.processing" class="btn-indigo" type="submit">
            <font-awesome-icon class="mr-2" icon="fas fa-save" />Simpan Perubahan
          </loading-button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import SelectInput from '@/Shared/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'
import Datepicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

export default {
  components: {
    Head,
    Link,
    LoadingButton,
    TextInput,
    SelectInput,
    Datepicker,
    FontAwesomeIcon,
  },
  layout: Layout,
  remember: 'form',
  props: {
    accountabilityPeriode: Object,
  },
  data() {
    return {
      form: this.$inertia.form({
        jenis: this.accountabilityPeriode.jenis,
        tanggal_awal_laporan: this.accountabilityPeriode.tanggal_awal_laporan,
        tanggal_akhir_laporan: this.accountabilityPeriode.tanggal_akhir_laporan,
        tanggal_awal_pj: this.accountabilityPeriode.tanggal_awal_pj,
        tanggal_akhir_pj: this.accountabilityPeriode.tanggal_akhir_pj,
        nominal_kcs: this.accountabilityPeriode.nominal_kcs,
        nominal_pts_prestasi: this.accountabilityPeriode.nominal_pts_prestasi,
        nominal_pts_non_prestasi: this.accountabilityPeriode.nominal_pts_non_prestasi,
        nominal_ptn_prestasi: this.accountabilityPeriode.nominal_ptn_prestasi,
        nominal_ptn_non_prestasi: this.accountabilityPeriode.nominal_ptn_non_prestasi,
        nominal_ptnl_prestasi: this.accountabilityPeriode.nominal_ptnl_prestasi,
        nominal_ptnl_non_prestasi: this.accountabilityPeriode.nominal_ptnl_non_prestasi,
        tahun_anggaran: this.accountabilityPeriode.tahun_anggaran,
      }),
      tanggal_awal_laporan: this.accountabilityPeriode.tanggal_awal_laporan ? new Date(this.accountabilityPeriode.tanggal_awal_laporan) : null,
      tanggal_akhir_laporan: this.accountabilityPeriode.tanggal_akhir_laporan ? new Date(this.accountabilityPeriode.tanggal_akhir_laporan) : null,
      tanggal_awal_pj: this.accountabilityPeriode.tanggal_awal_pj ? new Date(this.accountabilityPeriode.tanggal_awal_pj) : null,
      tanggal_akhir_pj: this.accountabilityPeriode.tanggal_akhir_pj ? new Date(this.accountabilityPeriode.tanggal_akhir_pj) : null,
    }
  },
  watch: {
    tanggal_awal_laporan(newVal) {
      this.form.tanggal_awal_laporan = newVal
    },
    tanggal_akhir_laporan(newVal) {
      this.form.tanggal_akhir_laporan = newVal
    },
    tanggal_awal_pj(newVal) {
      this.form.tanggal_awal_pj = newVal
    },
    tanggal_akhir_pj(newVal) {
      this.form.tanggal_akhir_pj = newVal
    },
  },
  methods: {
    update() {
      const formData = {
        ...this.form,
        tanggal_awal_laporan: this.tanggal_awal_laporan ? dayjs(this.tanggal_awal_laporan).format('YYYY-MM-DD') : null,
        tanggal_akhir_laporan: this.tanggal_akhir_laporan ? dayjs(this.tanggal_akhir_laporan).format('YYYY-MM-DD') : null,
        tanggal_awal_pj: this.tanggal_awal_pj ? dayjs(this.tanggal_awal_pj).format('YYYY-MM-DD') : null,
        tanggal_akhir_pj: this.tanggal_akhir_pj ? dayjs(this.tanggal_akhir_pj).format('YYYY-MM-DD') : null,
      }

      this.$inertia.put(this.route('accountability-periodes.update', this.accountabilityPeriode.id), formData)
    },
  },
}
</script>
